import { ref } from 'vue'

interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  duration?: number
  autoClose?: boolean
}

interface ToastOptions {
  type?: 'success' | 'error' | 'warning' | 'info'
  message?: string
  duration?: number
  autoClose?: boolean
}

const toasts = ref<Toast[]>([])

let toastCounter = 0

const generateId = (): string => {
  return `toast_${++toastCounter}_${Date.now()}`
}

export const useToast = () => {
  const addToast = (title: string, options: ToastOptions = {}) => {
    const toast: Toast = {
      id: generateId(),
      type: options.type || 'info',
      title,
      message: options.message,
      duration: options.duration || 5000,
      autoClose: options.autoClose !== false
    }

    toasts.value.push(toast)
    
    return toast.id
  }

  const removeToast = (id: string) => {
    const index = toasts.value.findIndex(toast => toast.id === id)
    if (index > -1) {
      toasts.value.splice(index, 1)
    }
  }

  const clearAll = () => {
    toasts.value = []
  }

  // Métodos de conveniência
  const success = (title: string, message?: string) => {
    return addToast(title, { type: 'success', message })
  }

  const error = (title: string, message?: string) => {
    return addToast(title, { type: 'error', message, duration: 8000 })
  }

  const warning = (title: string, message?: string) => {
    return addToast(title, { type: 'warning', message })
  }

  const info = (title: string, message?: string) => {
    return addToast(title, { type: 'info', message })
  }

  // Helper para tratar erros de API
  const handleApiError = (error: any, defaultMessage: string = 'Ocorreu um erro inesperado') => {
    let errorMessage = defaultMessage
    let errorTitle = 'Erro'

    if (error?.response?.data) {
      const data = error.response.data

      // Se tem estrutura padronizada de erro
      if (data.message) {
        errorMessage = data.message
      } else if (data.error && typeof data.error === 'string') {
        errorMessage = data.error
      }

      // Se é array de erros de validação
      if (data.errors && Array.isArray(data.errors)) {
        errorMessage = data.errors.join(', ')
      }

      // Status específicos
      switch (error.response.status) {
        case 400:
          errorTitle = 'Dados Inválidos'
          break
        case 401:
          errorTitle = 'Acesso Negado'
          errorMessage = 'Você não tem permissão para esta ação'
          break
        case 403:
          errorTitle = 'Permissão Insuficiente'
          break
        case 404:
          errorTitle = 'Não Encontrado'
          break
        case 409:
          errorTitle = 'Conflito'
          break
        case 422:
          errorTitle = 'Dados Inválidos'
          break
        case 500:
          errorTitle = 'Erro do Servidor'
          if (!data.message) {
            errorMessage = 'Erro interno do servidor. Tente novamente.'
          }
          break
      }
    } else if (error?.message) {
      errorMessage = error.message
    }

    return addToast(errorTitle, { type: 'error', message: errorMessage, duration: 8000 })
  }

  return {
    toasts: toasts.value,
    addToast,
    removeToast,
    clearAll,
    success,
    error,
    warning,
    info,
    handleApiError
  }
}